import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { Translate, getSortState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC } from 'app/shared/util/pagination.constants';
import { overrideSortStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './organisation.reducer';

export const Organisation = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [sortState, setSortState] = useState(
    overrideSortStateWithQueryParams(getSortState(pageLocation, 'id'), pageLocation.search),
  );

  const organisationList = useAppSelector(state => state.organisation.entities);
  const loading = useAppSelector(state => state.organisation.loading);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        sort: `${sortState.sort},${sortState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?sort=${sortState.sort},${sortState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [sortState.order, sortState.sort]);

  const sort = p => () => {
    setSortState({
      ...sortState,
      order: sortState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = sortState.sort;
    const order = sortState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="organisation-heading" data-cy="OrganisationHeading">
        <Translate contentKey="crawlerBackendApp.organisation.home.title">Organisations</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="crawlerBackendApp.organisation.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/organisation/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="crawlerBackendApp.organisation.home.createLabel">Create new Organisation</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {organisationList && organisationList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="crawlerBackendApp.organisation.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('organisationId')}>
                  <Translate contentKey="crawlerBackendApp.organisation.organisationId">Organisation Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('organisationId')} />
                </th>
                <th className="hand" onClick={sort('name')}>
                  <Translate contentKey="crawlerBackendApp.organisation.name">Name</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('name')} />
                </th>
                <th className="hand" onClick={sort('vatId')}>
                  <Translate contentKey="crawlerBackendApp.organisation.vatId">Vat Id</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('vatId')} />
                </th>
                <th className="hand" onClick={sort('organizationType')}>
                  <Translate contentKey="crawlerBackendApp.organisation.organizationType">Organization Type</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('organizationType')} />
                </th>
                <th className="hand" onClick={sort('status')}>
                  <Translate contentKey="crawlerBackendApp.organisation.status">Status</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('status')} />
                </th>
                <th className="hand" onClick={sort('enterpriseSize')}>
                  <Translate contentKey="crawlerBackendApp.organisation.enterpriseSize">Enterprise Size</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('enterpriseSize')} />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.organisation.customerAccount">Customer Account</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.organisation.parentOrganisation">
                    Parent Organisation
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.organisation.organisationAddress">
                    Organisation Address
                  </Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th>
                  <Translate contentKey="crawlerBackendApp.organisation.contact">Contact</Translate>{' '}
                  <FontAwesomeIcon icon="sort" />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {organisationList.map((organisation, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/organisation/${organisation.id}`} color="link" size="sm">
                      {organisation.id}
                    </Button>
                  </td>
                  <td>{organisation.organisationId}</td>
                  <td>{organisation.name}</td>
                  <td>{organisation.vatId}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.OrganizationType.${organisation.organizationType}`} />
                  </td>
                  <td>{organisation.status ? 'true' : 'false'}</td>
                  <td>
                    <Translate contentKey={`crawlerBackendApp.EnterpriseSize.${organisation.enterpriseSize}`} />
                  </td>
                  <td>
                    {organisation.customerAccount ? (
                      <Link to={`/customer-account/${organisation.customerAccount.id}`}>
                        {organisation.customerAccount.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {organisation.parentOrganisation ? (
                      <Link to={`/organisation/${organisation.parentOrganisation.id}`}>
                        {organisation.parentOrganisation.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {organisation.organisationAddress ? (
                      <Link to={`/address/${organisation.organisationAddress.id}`}>
                        {organisation.organisationAddress.id}
                      </Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td>
                    {organisation.contact ? (
                      <Link to={`/contact/${organisation.contact.id}`}>{organisation.contact.id}</Link>
                    ) : (
                      ''
                    )}
                  </td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/organisation/${organisation.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/organisation/${organisation.id}/edit`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() => (window.location.href = `/organisation/${organisation.id}/delete`)}
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="crawlerBackendApp.organisation.home.notFound">No Organisations found</Translate>
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default Organisation;
